<template>
	<Modal
		v-if="showShareModal"
		:allow-close="true"
		max-width="max-w-[540px]"
		:prevent-scrolling="false"
		@modal:close="resumeShareStore.hideShareModal"
	>
		<template #body>
			<h2 class="text-[24px] leading-[36px] font-semibold text-won-blue">
				Share {{ formatedResumeTitle }}
			</h2>
            
			<div class="mt-8">
				<label class="text-[14px] leading-[20px] font-bold text-won-blue">
					Add emails and hit Enter/Return
				</label>

				<div
					class="
                        mt-3 flex w-full cursor-text flex-wrap gap-2 rounded
                        border border-won-gray-light p-2.5 text-base text-won-black
                    "
					@click="focusOnInputHandler"
				>
					<div
						v-for="(email, index) in selectedUsers"
						:key="index"
						class="flex w-fit items-center gap-1 rounded-full border border-[#C2C4C6] px-1.5 py-1"
					>
						<span
							class="text-[12px] leading-[15px] text-won-blue"
						>
							{{ email }}
						</span>
                        
						<span
							class="cursor-pointer"
							@click="() => resumeShareStore.removeEmailHandler(index)"
						>
							<XMarkIcon class="size-4" />
						</span>
					</div>
                    
					<input
						ref="emailInputRef"
						v-model="email"
						placeholder="johndoe@gmail.com"
						class="
                            grow border-none p-0 outline-hidden placeholder:text-[#686E77]
                            focus:border-won-green focus:ring-0 focus:outline-hidden
                        "
						@keydown.enter="resumeShareStore.addEmailHandler"
						@change="resumeShareStore.emailInputChangeHandler"
						@blur="resumeShareStore.addEmailHandler"
					/>
				</div>
                
				<div class="mt-1 h-4">
					<span class="block text-small text-red-500">
						{{ validationErrors.email }}
					</span>
				</div>
			</div>

			<div
				v-if="selectedUsers.length > 0"
				class="mt-4"
			>
				<RadioGroup
					v-model="invitedUsersAccessRights"
					class="flex gap-4"
				>
					<RadioGroupOption
						v-for="option in ACCESS_MENU_ACTIONS"
						:key="option.value"
						v-slot="{ checked }"
						:value="option.value"
						:disabled="option?.disabled"
					>
						<div
							class="
								flex items-center gap-2 rounded border px-6 py-3 text-center text-won-dark-blue
								shadow-radio-card
							"
							:class="[
								checked
									? 'border-won-green bg-won-green-light'
									: 'border-[rgba(95, 95, 0.15)]  bg-white',
								option?.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
							]"
						>
							<div
								class="flex size-4 items-center justify-center rounded-full"
								:class="checked ? 'bg-won-green' : 'border border-[#D8D8E9]'" 
							>
								<div
									v-if="checked"
									class="size-1.5 rounded-full bg-white"
								/>
							</div>
                            
							<span class="text-[15px] leading-[24px] font-semibold">
								{{ option.actionText }}
							</span>
						</div>
					</RadioGroupOption>
				</RadioGroup>
			</div>
            
			<div
				v-else-if="sharedWith.length > 0"
				class="mt-4"
			>
				<h3 class="text-[14px] leading-[20px] font-semibold text-won-blue">
					People with access
				</h3>
                
				<div
					v-if="selectedUsers.length === 0"
					class="mt-3 block text-small text-won-dark"
				>
					Add people using email address
				</div>
                
				<div class="mt-3 flex h-full max-h-[200px] flex-col gap-4 overflow-y-auto pr-2">
					<AccessPreview
						v-for="user in sharedWith"
						:key="user.id"
						:user-id="user?.id"
						:user-email="user.email"
						:user-name="user?.name || '-'"
						:permission="user?.permission"
						:options="ACCESS_MENU_ACTIONS"
						:display-map="USER_PERMISSION_DISPLAY_MAP"
					/>
				</div>
			</div>
            
			<div
				v-if="selectedUsers.length === 0"
				class="mt-4"
			>
				<h3
					class="text-[14px] leading-[20px] font-semibold text-won-blue"
				>
					General access
				</h3>
                
				<div class="mt-3">
					<div class="flex max-h-9 items-center justify-between">
						<div class="flex items-center gap-3">
							<div class="size-9 rounded-full bg-won-sand-light p-1.5">
								<GlobeAltIcon
									class="size-6 text-won-dark"
								/>
							</div>
                            
							<div class="">
								<span class="text-base text-won-dark-blue">
									<AccessPreviewMenu
										v-model="generalAccessValue"
										:options="GENERAL_ACCESS_OPTIONS"
										:display-map="GENERAL_PERMISSION_DISPLAY_MAP"
										@update:model-value="resumeShareStore.generalAccessChangeHandler"
									/>
								</span>
                
								<span
									class="mt-1 block text-small text-won-dark"
								>
									<span v-if="generalAccessValue === 'restricted'">
										Only people with access can open with the link
									</span>
									<span v-if="generalAccessValue === 'anyoneWithLink'">
										Anyone on the internet with the link can view
									</span>
								</span>
							</div>
						</div>

						<AccessPreviewMenu
							v-if="generalAccessValue === 'anyoneWithLink'"
							v-model="anyoneWithLinkAccessLevel"
							:options="ACCESS_MENU_ACTIONS"
							:display-map="USER_PERMISSION_DISPLAY_MAP"
						/>
					</div>
				</div>
			</div>
            
			<div class="mt-14 flex items-stretch justify-end gap-5 text-base font-medium">
				<CopyShareLinkBtn
					:disabled="loadingCopyShareLink || loadingShareResume"
				/>
                
				<button
					class="
                        relative flex items-center justify-center rounded border border-won-green 
                        bg-won-green px-10 py-1.5
                        hover:opacity-90 disabled:border-won-gray disabled:bg-won-gray disabled:opacity-40
                    "
					:disabled="loadingShareResume || loadingCopyShareLink"
					@click="resumeShareStore.shareResumeWithEmails"
				>
					<span
						class="text-white"
						:class="{'opacity-0' : loadingShareResume && !loadingCopyShareLink}"
					>
						<span v-if="selectedUsers.length > 0">Share</span>
						<span v-else>Done</span>
					</span>

					<span
						v-if="!loadingCopyShareLink"
						class="absolute"
					>
						<LoadingIcon
							:class="{'opacity-0' : !loadingShareResume && !loadingCopyShareLink}"
						/>
					</span>
				</button>
			</div>
		</template>
	</Modal>
</template>

<script setup>
import { toRefs, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { RadioGroup, RadioGroupOption } from '@headlessui/vue';
import { XMarkIcon, GlobeAltIcon } from '@heroicons/vue/24/outline';
import { useResumeShare } from '../../store/share-resume.store';

import {
	USER_PERMISSION_DISPLAY_MAP,
	ACCESS_MENU_ACTIONS,
	GENERAL_PERMISSION_DISPLAY_MAP,
	GENERAL_ACCESS_OPTIONS
} from '../../utils/share-resume.storage';

import Modal from '../../components/modal.vue';
import AccessPreview from '../../components/resume-tool/access-preview.vue';
import AccessPreviewMenu from '../../components/resume-tool/access-preview-menu.vue';
import CopyShareLinkBtn from '../../components/resume-tool/copy-share-link-btn.vue';

import LoadingIcon from '../../icons/loading-icon.vue';

const props = defineProps({
	resume: {
		type: Object,
		default: null
	},
	resumeTitle: {
		type: String,
		default: ''
	}
});

const { resumeTitle } = toRefs(props);

const resumeShareStore = useResumeShare();
const {
	email,
	sharedWith,
	emailInputRef,
	selectedUsers,
	showShareModal,
	validationErrors,
	loadingShareResume,
	generalAccessValue,
	loadingCopyShareLink,
	invitedUsersAccessRights,
	anyoneWithLinkAccessLevel,
} = storeToRefs(resumeShareStore);

const focusOnInputHandler = () => {
	emailInputRef.value.focus();
};

const formatedResumeTitle = computed(() => {
	return `"${resumeTitle.value?.replace('<b>', '')?.replace('</b>', '')}"`;
});
</script>
